import { useCallback, useMemo, useSyncExternalStore } from 'react';

import {
  getColorSchemeSnapshot,
  getServerColorSchemeSnapshot,
  isEmbedPathname,
  resolveColorScheme,
  subscribeColorScheme,
  writeStoredColorSchemePreference,
  type ColorScheme,
  type ColorSchemePreference,
} from '@/theme/colorScheme';

type UseColorScheme = {
  preference: ColorSchemePreference;
  resolvedScheme: ColorScheme;
  setPreference: (preference: ColorSchemePreference) => void;
};

function currentPathname(): string {
  return typeof window === 'undefined' ? '' : window.location.pathname;
}

const useColorScheme = (): UseColorScheme => {
  const snapshot = useSyncExternalStore(
    subscribeColorScheme,
    getColorSchemeSnapshot,
    getServerColorSchemeSnapshot,
  );
  const isEmbed = isEmbedPathname(currentPathname());

  const resolvedScheme = useMemo(
    () =>
      resolveColorScheme({
        isEmbed,
        storedPreference: snapshot.preference,
        embedOverride: snapshot.embedOverride,
        systemScheme: snapshot.systemScheme,
      }),
    [
      isEmbed,
      snapshot.embedOverride,
      snapshot.preference,
      snapshot.systemScheme,
    ],
  );

  const setPreference = useCallback((next: ColorSchemePreference) => {
    writeStoredColorSchemePreference(next);
  }, []);

  return {
    preference: isEmbed
      ? (snapshot.embedOverride ?? 'system')
      : snapshot.preference,
    resolvedScheme,
    setPreference,
  };
};

export default useColorScheme;
